import React, { createContext, useEffect, useReducer } from 'react'
import axios from '../../axios'
import { MatxLoading } from '../components'
import { API_URI } from '../../config'
import { useNavigate } from 'react-router-dom'

const initialState = {
    isAuthenticated: false,
    isInitialised: false,
    user: null,
    isImpersonating: false,
}

const IMPERSONATION_TOKEN_KEY = 'IMPERSONATOR_ACCESS_TOKEN'
const IMPERSONATION_ROLE_KEY = 'IMPERSONATOR_ROLE'

const setSession = (accessToken) => {
    if (accessToken) {
        localStorage.setItem('accessToken', accessToken)
        axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`
    } else {
        localStorage.removeItem('accessToken')
        delete axios.defaults.headers.common.Authorization
    }
}

const reducer = (state, action) => {
    switch (action.type) {
        case 'INIT': {
            const { isAuthenticated, user } = action.payload

            return {
                ...state,
                isAuthenticated,
                isInitialised: true,
                user,
                isImpersonating: !!user?.impersonatedBy || !!user?.isImpersonating,
            }
        }
        case 'LOGIN': {
            const { user } = action.payload

            return {
                ...state,
                isAuthenticated: true,
                isInitialised: true,
                user,
                isImpersonating: !!user?.impersonatedBy || !!user?.isImpersonating,
            }
        }
        case 'UPDATE_USER': {
            const { user } = action.payload

            return {
                ...state,
                user,
                isImpersonating: !!user?.impersonatedBy || !!user?.isImpersonating,
            }
        }
        case 'LOGOUT': {
            return {
                ...state,
                isAuthenticated: false,
                user: null,
                isImpersonating: false,
            }
        }
        case 'REGISTER': {
            const { user } = action.payload

            return {
                ...state,
                isAuthenticated: true,
                isInitialised: true,
                user,
                isImpersonating: false,
            }
        }
        default: {
            return { ...state }
        }
    }
}

const AuthContext = createContext({
    ...initialState,
    method: 'JWT',
    login: () => Promise.resolve(),
    logout: () => { },
    register: () => Promise.resolve(),
    loginAsClient: () => Promise.resolve(),
    exitLoginAsClient: () => Promise.resolve(),
})

export const AuthProvider = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, initialState)

    const navigate = useNavigate()

    const login = async (email, mfaCode) => {
        window.localStorage.removeItem(IMPERSONATION_TOKEN_KEY)
        window.localStorage.removeItem(IMPERSONATION_ROLE_KEY)
        const response = await axios.post(
            `${API_URI}/api/admin/auth/verify-otp`,
            {
                email,
                mfaCode,
            }
        )

        const { token, user } = response?.data
        window.localStorage.setItem('ROLE', user.role)

        setSession(token)

        // First dispatch with initial user data
        dispatch({
            type: 'LOGIN',
            payload: {
                user,
            },
        })

        // Fetch full user profile (including club data) to ensure all data is available
        try {
            const { data: fullUserData } = await axios.get(
                `${API_URI}/api/admin/me`,
                {
                    headers: {
                        authorization: `Bearer ${token}`,
                    },
                }
            )

            // Update with complete user data
            dispatch({
                type: 'UPDATE_USER',
                payload: {
                    user: fullUserData,
                },
            })
        } catch (err) {
            // If fetching full user data fails, continue with the initial user data
            console.warn('Failed to fetch full user profile:', err)
        }
    }

    const loginAsClient = async (id) => {
        const currentToken = window.localStorage.getItem('accessToken')
        const currentRole = window.localStorage.getItem('ROLE')
        if (currentToken && currentRole === 'SA') {
            // Keep a local fallback so SA can always return even if exit API fails.
            window.localStorage.setItem(IMPERSONATION_TOKEN_KEY, currentToken)
            window.localStorage.setItem(IMPERSONATION_ROLE_KEY, currentRole)
        }

        const response = await axios.post(
            `${API_URI}/api/admin/auth/login-as-client/${id}`
        )

        const { token, user } = response?.data
        window.localStorage.setItem('ROLE', user.role)

        setSession(token)

        // First dispatch with initial user data
        dispatch({
            type: 'LOGIN',
            payload: {
                user,
            },
        })

        // Fetch full user profile (including club data) to ensure all data is available
        try {
            const { data: fullUserData } = await axios.get(
                `${API_URI}/api/admin/me`
            )

            // Update with complete user data
            dispatch({
                type: 'UPDATE_USER',
                payload: {
                    user: fullUserData,
                },
            })
        } catch (err) {
            console.warn('Failed to fetch full user profile:', err)
        }

        return true
    }

    const exitLoginAsClient = async () => {
        try {
            const response = await axios.post(
                `${API_URI}/api/admin/auth/exit-login-as-client`
            )

            const { token, user } = response?.data
            window.localStorage.setItem('ROLE', user.role)
            setSession(token)

            dispatch({
                type: 'LOGIN',
                payload: {
                    user,
                },
            })

            try {
                const { data: fullUserData } = await axios.get(
                    `${API_URI}/api/admin/me`
                )

                dispatch({
                    type: 'UPDATE_USER',
                    payload: {
                        user: fullUserData,
                    },
                })
            } catch (err) {
                console.warn('Failed to fetch full user profile:', err)
            }
        } catch (err) {
            const fallbackToken = window.localStorage.getItem(IMPERSONATION_TOKEN_KEY)
            const fallbackRole = window.localStorage.getItem(IMPERSONATION_ROLE_KEY)

            if (!fallbackToken || fallbackRole !== 'SA') {
                throw err
            }

            setSession(fallbackToken)
            window.localStorage.setItem('ROLE', 'SA')
        }

        window.localStorage.removeItem(IMPERSONATION_TOKEN_KEY)
        window.localStorage.removeItem(IMPERSONATION_ROLE_KEY)
        window.location.href = '/dashboard/analytics'
        return true
    }

    const register = async (email, password) => {
        window.localStorage.removeItem(IMPERSONATION_TOKEN_KEY)
        window.localStorage.removeItem(IMPERSONATION_ROLE_KEY)
        const response = await axios.post(
            `${API_URI}/api/admin/auth/register`,
            {
                email,
                password,
            }
        )

        const { accessToken, user } = response.data

        setSession(accessToken)

        dispatch({
            type: 'REGISTER',
            payload: {
                user,
            },
        })
    }

    const logout = () => {
        window.localStorage.removeItem('ROLE')
        window.localStorage.removeItem(IMPERSONATION_TOKEN_KEY)
        window.localStorage.removeItem(IMPERSONATION_ROLE_KEY)
        setSession(null)
        dispatch({ type: 'LOGOUT' })
    }

      const initAuth = async (isMounted, abortController) => {
            try {
                const accessToken = window.localStorage.getItem('accessToken')

                if (accessToken) {
                   setSession(accessToken)

                    const { data } = await axios.get(
                        `${API_URI}/api/admin/me`,
                        {
                            signal: abortController.signal,
                        }
                    )
                    if (!isMounted) return

                    dispatch({
                        type: 'INIT',
                        payload: {
                            isAuthenticated: true,
                            user: {
                                ...data,
                            },
                        },
                    })
                } else {
                    if (!isMounted) return

                    dispatch({
                        type: 'INIT',
                        payload: {
                            isAuthenticated: false,
                            user: null,
                        },
                    })
                    window.localStorage.removeItem('accessToken')
                    window.localStorage.removeItem('ROLE')
                    navigate('/')
                }
            } catch (err) {
                // Ignore abort errors
                if (err.name === 'AbortError' || err.name === 'CanceledError') return

                if (!isMounted) return

                dispatch({
                    type: 'INIT',
                    payload: {
                        isAuthenticated: false,
                        user: null,
                    },
                })
                window.localStorage.removeItem('accessToken')
                navigate('/')
            }
        }

    useEffect(() => {
        let isMounted = true
        const abortController = new AbortController()
        initAuth(isMounted, abortController)
        return () => {
            isMounted = false
            abortController.abort()
        }
    }, [])

    if (!state.isInitialised) {
        return <MatxLoading />
    }

    return (
        <AuthContext.Provider
            value={{
                ...state,
                method: 'JWT',
                login,
                logout,
                register,
                loginAsClient,
                exitLoginAsClient,
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export default AuthContext
